import {useMemo} from 'react';
import cloneDeep from 'lodash/cloneDeep';

export interface INavigationLinkImagesOptions {
	includeCategoryImage?: boolean;
	requireCategoryImage?: boolean;
	includePersonalizedItems?: boolean;
	includeFlatNavigationItem?: boolean;
}

type TNavigationImage = {
	id: string;
	image: any;
	customSettings: Record<string, any>;
	showHoverImage?: boolean;
	hoverLabelVariant?: string;
	color?: string;
};

type TPlaceholderData = {
	id: string;
	categoryImage?: any;
	images: TNavigationImage[];
};

const PLACEHOLDER_ID = 'NavigationItemLinkImage';

const DEFAULT_OPTIONS: Required<INavigationLinkImagesOptions> = {
	includeCategoryImage: false,
	requireCategoryImage: false,
	includePersonalizedItems: true,
	includeFlatNavigationItem: false,
};

const isNavTypename = (typename: string | undefined, includeFlatNav: boolean): boolean =>
	typename === 'NavigationItem' || (includeFlatNav && typename === 'FlatNavigationItem');

const collectImages = (
	collection: any,
	images: TNavigationImage[],
	opts: Required<INavigationLinkImagesOptions>,
): void => {
	collection?.items?.forEach((cItem: any) => {
		const nested =
			cItem?.itemsCollection
			|| cItem?.content?.itemsCollection
			|| cItem?.contentCollection
			|| cItem?.subItemsCollection
			|| (opts.includePersonalizedItems ? cItem?.personalizedItemsCollection : undefined);

		if (nested) {
			collectImages(nested, images, opts);
		}

		const mediaOrImage = cItem?.media || cItem?.image;

		if (isNavTypename(cItem?.__typename, opts.includeFlatNavigationItem) && mediaOrImage) {
			images.push({
				id: cItem.sys.id,
				image: mediaOrImage,
				customSettings: cItem.customSettings || {},
				showHoverImage: cItem.showHoverImage,
				hoverLabelVariant: cItem.hoverLabelVariant,
				color: cItem.color || cItem.Color,
			});
		}

		if (opts.includePersonalizedItems) {
			const pMedia = cItem?.item?.media || cItem?.item?.image;

			if (isNavTypename(cItem?.item?.__typename, opts.includeFlatNavigationItem) && pMedia) {
				images.push({
					id: cItem.item.sys.id,
					image: pMedia,
					customSettings: cItem.item.customSettings || {},
					showHoverImage: cItem.item.showHoverImage,
					hoverLabelVariant: cItem.item.hoverLabelVariant,
					color: cItem.item.color || cItem.item.Color,
				});
			}
		}
	});
};

const injectIntoPlaceholders = (collection: any, data: TPlaceholderData): void => {
	collection?.items?.forEach((cItem: any, idx: number) => {
		const nested = cItem?.itemsCollection || cItem?.content?.itemsCollection || cItem?.contentCollection;

		if (nested) {
			injectIntoPlaceholders(nested, data);
		}

		if (cItem?.__typename === 'Placeholder' && cItem?.elementId === PLACEHOLDER_ID) {
			collection.items[idx].data = data;
		}
	});
};

/**
 * Populates NavigationItemLinkImage placeholders in header navigation data
 * with images collected from NavigationItem nodes in each nav template.
 */
const useNavigationLinkImages = <T = unknown>(
	navigationData: T | undefined,
	options?: INavigationLinkImagesOptions,
): T | undefined => {
	const opts = useMemo(
		() => ({...DEFAULT_OPTIONS, ...options}),
		[
			options?.includeCategoryImage,
			options?.requireCategoryImage,
			options?.includePersonalizedItems,
			options?.includeFlatNavigationItem,
		],
	);

	return useMemo(() => {
		if (!navigationData) {
			return navigationData;
		}

		const data = cloneDeep(navigationData) as any;
		const navItems = data?.headerCollection?.items?.[0]?.navigation?.subItemsCollection?.items;

		if (!navItems?.length) {
			return data as T;
		}

		const processItem = (navItem: any) => {
			if (!navItem?.template) {
				return;
			}

			if (opts.requireCategoryImage && !(navItem.media || navItem.image)) {
				return;
			}

			const images: TNavigationImage[] = [];

			collectImages(navItem.template.itemsCollection, images, opts);

			const placeholderData: TPlaceholderData = {id: navItem.sys.id, images};

			if (opts.includeCategoryImage) {
				placeholderData.categoryImage = navItem.media || navItem.image;
			}

			injectIntoPlaceholders(navItem.template.itemsCollection, placeholderData);
		};

		navItems.forEach((item: any) => {
			processItem(item);
			item.personalizedItemsCollection?.items?.forEach((pItem: any) => {
				processItem(pItem?.item);
			});
		});

		return data as T;
	}, [navigationData, opts]);
};

export default useNavigationLinkImages;
